import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Users, MessageSquare, BarChart3, Lock, LogOut, 
  ChevronDown, ChevronUp, TrendingUp, DollarSign, KeyRound
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export const AdminPanel = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('stats');
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  
  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post(`/admin/login?password=${encodeURIComponent(password)}`);
      localStorage.setItem('admin_token', response.data.token);
      setAuthenticated(true);
      toast.success('Admin access granted');
      fetchData();
    } catch (error) {
      toast.error('Invalid admin password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAuthenticated(false);
  };

  const fetchData = async () => {
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) return;

    const config = { headers: { Authorization: `Bearer ${adminToken}` } };

    try {
      const [statsRes, usersRes, feedbacksRes] = await Promise.all([
        api.get('/admin/stats', config),
        api.get('/admin/users', config),
        api.get('/admin/feedback', config)
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setFeedbacks(feedbacksRes.data.feedbacks);
    } catch (error) {
      if (error.response?.status === 401) {
        setAuthenticated(false);
        localStorage.removeItem('admin_token');
        toast.error('Session expired');
      } else {
        toast.error('Failed to load data');
      }
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail || !resetPassword) {
      toast.error('Please enter both email and new password');
      return;
    }
    
    setResetting(true);
    const adminToken = localStorage.getItem('admin_token');
    
    try {
      await api.post('/admin/reset-password', 
        { email: resetEmail, new_password: resetPassword },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(`Password reset for ${resetEmail}`);
      setResetEmail('');
      setResetPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      setAuthenticated(true);
      fetchData();
    }
  }, []);

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-panel rounded-2xl p-8">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Lock className="w-8 h-8 text-[#D4AF37]" />
              <h1 className="font-serif text-2xl text-[#F8F5F2]">Admin Access</h1>
            </div>
            
            <form onSubmit={handleLogin}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-[#F8F5F2] font-sans placeholder:text-[#6B7280] focus:outline-none focus:border-[#D4AF37]/50 mb-4"
                data-testid="admin-password-input"
              />
              <button
                type="submit"
                disabled={loading || !password}
                className="w-full py-3 rounded-xl bg-[#D4AF37] text-[#030305] font-sans font-semibold hover:bg-[#D4AF37]/90 transition-all disabled:opacity-50"
                data-testid="admin-login-button"
              >
                {loading ? 'Verifying...' : 'Access Admin Panel'}
              </button>
            </form>
            
            <button
              onClick={() => navigate('/')}
              className="w-full mt-4 text-[#A1A1AA] hover:text-[#F8F5F2] font-sans text-sm transition-colors"
            >
              ← Back to App
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-serif text-3xl text-[#F8F5F2]">IN Admin Panel</h1>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 text-[#A1A1AA] hover:text-red-400 hover:border-red-400/30 transition-all font-sans text-sm"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { id: 'stats', label: 'Stats', icon: BarChart3 },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'feedback', label: 'Feedback', icon: MessageSquare },
            { id: 'reset', label: 'Reset Password', icon: KeyRound }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-sans text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-[#D4AF37] text-[#030305]'
                  : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 hover:text-[#F8F5F2]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="glass-panel rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                  <span className="font-sans text-[#A1A1AA] text-sm">Total Users</span>
                </div>
                <p className="font-serif text-4xl text-[#F8F5F2]">{stats.total_users}</p>
              </div>
              
              <div className="glass-panel rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                  <span className="font-sans text-[#A1A1AA] text-sm">Assessments</span>
                </div>
                <p className="font-serif text-4xl text-[#F8F5F2]">{stats.total_assessments}</p>
              </div>
              
              <div className="glass-panel rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
                  <span className="font-sans text-[#A1A1AA] text-sm">Feedbacks</span>
                </div>
                <p className="font-serif text-4xl text-[#F8F5F2]">{stats.total_feedbacks}</p>
              </div>
              
              <div className="glass-panel rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  <span className="font-sans text-[#A1A1AA] text-sm">Would Pay $10</span>
                </div>
                <p className="font-serif text-4xl text-green-400">{stats.would_pay_breakdown?.yes || 0}</p>
              </div>
            </div>

            {/* Would Pay Breakdown */}
            <div className="glass-panel rounded-xl p-6">
              <h3 className="font-sans text-[#F8F5F2] font-semibold mb-4">Would Pay $10/month?</h3>
              <div className="flex gap-8">
                <div className="text-center">
                  <p className="font-serif text-3xl text-green-400">{stats.would_pay_breakdown?.yes || 0}</p>
                  <p className="font-sans text-sm text-[#A1A1AA]">Yes</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-3xl text-yellow-400">{stats.would_pay_breakdown?.maybe || 0}</p>
                  <p className="font-sans text-sm text-[#A1A1AA]">Maybe</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-3xl text-red-400">{stats.would_pay_breakdown?.no || 0}</p>
                  <p className="font-sans text-sm text-[#A1A1AA]">No</p>
                </div>
              </div>
            </div>

            {/* Average Ratings */}
            {stats.average_ratings && (
              <div className="glass-panel rounded-xl p-6">
                <h3 className="font-sans text-[#F8F5F2] font-semibold mb-4">Average Ratings (out of 5)</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { label: 'Questions Clear', key: 'avg_questions_sense' },
                    { label: 'Sun Map Accurate', key: 'avg_sun_map_accurate' },
                    { label: 'Reading Personal', key: 'avg_reading_personal' },
                    { label: 'Action Useful', key: 'avg_action_useful' },
                    { label: 'Would Use Weekly', key: 'avg_would_use_weekly' }
                  ].map((item) => (
                    <div key={item.key} className="text-center">
                      <p className="font-serif text-2xl text-[#D4AF37]">
                        {stats.average_ratings[item.key]?.toFixed(1) || '-'}
                      </p>
                      <p className="font-sans text-xs text-[#A1A1AA]">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass-panel rounded-xl overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-black/30">
                  <tr>
                    <th className="text-left px-6 py-4 font-sans text-sm text-[#A1A1AA] font-medium">Email</th>
                    <th className="text-left px-6 py-4 font-sans text-sm text-[#A1A1AA] font-medium">Name</th>
                    <th className="text-left px-6 py-4 font-sans text-sm text-[#A1A1AA] font-medium">Signed Up</th>
                    <th className="text-left px-6 py-4 font-sans text-sm text-[#A1A1AA] font-medium">Assessments</th>
                    <th className="text-left px-6 py-4 font-sans text-sm text-[#A1A1AA] font-medium">Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={user.id} className={index % 2 === 0 ? 'bg-black/10' : ''}>
                      <td className="px-6 py-4 font-sans text-sm text-[#F8F5F2]">{user.email}</td>
                      <td className="px-6 py-4 font-sans text-sm text-[#F8F5F2]">{user.name}</td>
                      <td className="px-6 py-4 font-sans text-sm text-[#A1A1AA]">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 font-sans text-sm text-[#D4AF37]">{user.assessment_count}</td>
                      <td className="px-6 py-4">
                        {user.has_feedback ? (
                          <span className="px-2 py-1 rounded-full bg-green-400/20 text-green-400 text-xs font-sans">Done</span>
                        ) : (
                          <span className="px-2 py-1 rounded-full bg-white/10 text-[#A1A1AA] text-xs font-sans">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <div className="text-center py-12 text-[#A1A1AA] font-sans">No users yet</div>
              )}
            </div>
          </motion.div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {feedbacks.length === 0 ? (
              <div className="glass-panel rounded-xl p-12 text-center">
                <MessageSquare className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
                <p className="font-sans text-[#A1A1AA]">No feedback submitted yet</p>
              </div>
            ) : (
              feedbacks.map((feedback) => (
                <div key={feedback.id} className="glass-panel rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedFeedback(expandedFeedback === feedback.id ? null : feedback.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span className="font-sans text-[#F8F5F2]">{feedback.user_email}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-sans ${
                        feedback.would_pay_10 === 'yes' 
                          ? 'bg-green-400/20 text-green-400'
                          : feedback.would_pay_10 === 'maybe'
                          ? 'bg-yellow-400/20 text-yellow-400'
                          : 'bg-red-400/20 text-red-400'
                      }`}>
                        ${feedback.would_pay_10}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-sans text-sm text-[#A1A1AA]">
                        {new Date(feedback.submitted_at).toLocaleDateString()}
                      </span>
                      {expandedFeedback === feedback.id ? (
                        <ChevronUp className="w-4 h-4 text-[#A1A1AA]" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[#A1A1AA]" />
                      )}
                    </div>
                  </button>
                  
                  {expandedFeedback === feedback.id && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: 'auto' }}
                      className="px-6 pb-6 border-t border-white/5"
                    >
                      <div className="grid grid-cols-5 gap-4 py-4">
                        <div className="text-center">
                          <p className="font-serif text-xl text-[#D4AF37]">{feedback.questions_made_sense}</p>
                          <p className="font-sans text-xs text-[#A1A1AA]">Questions Clear</p>
                        </div>
                        <div className="text-center">
                          <p className="font-serif text-xl text-[#D4AF37]">{feedback.sun_map_accurate}</p>
                          <p className="font-sans text-xs text-[#A1A1AA]">Sun Map</p>
                        </div>
                        <div className="text-center">
                          <p className="font-serif text-xl text-[#D4AF37]">{feedback.reading_personal}</p>
                          <p className="font-sans text-xs text-[#A1A1AA]">Personal</p>
                        </div>
                        <div className="text-center">
                          <p className="font-serif text-xl text-[#D4AF37]">{feedback.action_useful}</p>
                          <p className="font-sans text-xs text-[#A1A1AA]">Useful</p>
                        </div>
                        <div className="text-center">
                          <p className="font-serif text-xl text-[#D4AF37]">{feedback.would_use_weekly}</p>
                          <p className="font-sans text-xs text-[#A1A1AA]">Weekly</p>
                        </div>
                      </div>
                      
                      {feedback.what_confused && (
                        <div className="mt-4">
                          <p className="font-sans text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">What Confused</p>
                          <p className="font-sans text-sm text-[#F8F5F2]">{feedback.what_confused}</p>
                        </div>
                      )}
                      
                      {feedback.what_would_add && (
                        <div className="mt-4">
                          <p className="font-sans text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">What Would Add</p>
                          <p className="font-sans text-sm text-[#F8F5F2]">{feedback.what_would_add}</p>
                        </div>
                      )}
                      
                      {feedback.other_comments && (
                        <div className="mt-4">
                          <p className="font-sans text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Other Comments</p>
                          <p className="font-sans text-sm text-[#F8F5F2]">{feedback.other_comments}</p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              ))
            )}
          </motion.div>
        )}

        {/* Password Reset Tab */}
        {activeTab === 'reset' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-md"
          >
            <div className="glass-panel rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <KeyRound className="w-6 h-6 text-[#D4AF37]" />
                <h2 className="font-serif text-xl text-[#F8F5F2]">Reset User Password</h2>
              </div>
              
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div>
                  <label className="font-sans text-xs text-[#A1A1AA] uppercase tracking-wider block mb-2">
                    User Email
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-[#F8F5F2] font-sans placeholder:text-[#6B7280] focus:outline-none focus:border-[#D4AF37]/50"
                    data-testid="reset-email-input"
                  />
                </div>
                
                <div>
                  <label className="font-sans text-xs text-[#A1A1AA] uppercase tracking-wider block mb-2">
                    New Password
                  </label>
                  <input
                    type="text"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-[#F8F5F2] font-sans placeholder:text-[#6B7280] focus:outline-none focus:border-[#D4AF37]/50"
                    data-testid="reset-password-input"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={resetting || !resetEmail || !resetPassword}
                  className="w-full py-3 rounded-xl bg-[#D4AF37] text-[#030305] font-sans font-semibold hover:bg-[#D4AF37]/90 transition-all disabled:opacity-50"
                  data-testid="reset-password-button"
                >
                  {resetting ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
              
              <p className="font-sans text-xs text-[#6B7280] mt-4">
                This will immediately update the user's password. They can then log in with the new password.
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
